import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Modal } from "components/designV2/modal/modal";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import type { StaffProfileEmploymentCurrent } from "services/api/company/staff.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import { useCompanySubscription } from "web-apps/company/components/common/hooks/useCompanySubscription";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { StaffProfilePanelProps } from "./profilePanel.contract";
import { useStaffProfilePanelLogic } from "./profilePanel.logic";
import { useStaffProfilePanelState } from "./profilePanel.state.logic";
import { useProfilePanelActionsLogic } from "./profilePanel.actions.logic";
import { useUnsavedEmploymentGuard } from "./profilePanel.unsavedEmploymentGuard.hook";
import { ProfilePanelHeader } from "./layout/profilePanel.header.component";
import { ProfilePanelTabs } from "./layout/profilePanel.tabs.component";
import { ProfilePanelProfileSection } from "./domains/profile/profilePanel.profileSection.component";
import { ProfilePanelDeleteModal } from "./domains/profile/profilePanel.deleteModal.component";
import { ProfilePanelTeamModal } from "./domains/profile/profilePanel.teamModal.component";
import { ProfilePanelEmploymentSection } from "./domains/employment/profilePanel.employmentSection.component";
import { ProfilePanelEndEmploymentModal } from "./domains/employment/profilePanel.endEmploymentModal.component";
import { ProfilePanelChangeToPermanentModal } from "./domains/employment/profilePanel.changeToPermanentModal.component";
import { ProfilePanelHistorySection } from "./domains/history/profilePanel.historySection.component";
import { ProfilePanelAgreementsSection } from "./domains/agreements/profilePanel.agreementsSection.component";
import { ProfilePanelFooter } from "./layout/profilePanel.footer.component";
import { ProfilePanelSalaryModal } from "./domains/salary/profilePanel.salaryModal.component";
import { ProfilePanelTaxModal } from "./domains/tax/profilePanel.taxModal.component";
import { ProfilePanelPaymentMethodModal } from "./domains/payment/profilePanel.paymentMethodModal.component";
import { isFutureEmploymentStartDate } from "./domains/profilePanel.history.logic";
import {
  Message,
  WarningBanner,
  WarningBody,
  WarningTitle,
} from "./layout/profilePanel.styled";

export const StaffProfilePanel = (
  props: StaffProfilePanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const onClose = props.onClose;

  const [companyState] = useCompanyStore();
  const subscription = useCompanySubscription({
    companyId: props.companyId,
    isEnabled: props.isOpen,
  });

  const logic = useStaffProfilePanelLogic({
    companyId: props.companyId,
    workerId: props.workerId,
    isOpen: props.isOpen,
    onSaved: props.onSaved,
  });

  const reloadProfile = logic.reloadProfile;

  const avatarUrl = useWorkerAvatar({
    workerExternalId: logic.profile?.externalId,
    isWorker: true,
  });

  const panelState = useStaffProfilePanelState({
    isOpen: props.isOpen,
    workerId: props.workerId,
    companyId: props.companyId,
    companyAddress: companyState.company?.address,
    initialTab: props.initialTab,
    profile: logic.profile,
    unitTeamOptions: props.unitTeamOptions,
    subscription,
    companySettings: companyState.company?.companySettings ?? [],
    t,
    reloadProfile: logic.reloadProfile,
    onWorkerUpdated: props.onWorkerUpdated,
  });
  const updatePersonalField = panelState.updatePersonalField;
  const editedTeamIds = panelState.editedPersonalDetails.teamIds;

  const actions = useProfilePanelActionsLogic({
    isOpen: props.isOpen,
    companyId: props.companyId,
    workerId: props.workerId,
    workGroups: logic.profile?.workGroups,
    editedTeamIds,
    updatePersonalField,
    reloadProfile,
    onClose: props.onClose,
    onWorkerUpdated: props.onWorkerUpdated,
    onWorkerDeleted: props.onWorkerDeleted,
    translate: t,
    alertDispatch,
  });
  const hasUnsavedNewEmploymentChanges =
    panelState.hasUnsavedNewEmploymentChanges;
  const unsavedEmploymentGuard = useUnsavedEmploymentGuard({
    activeTab: panelState.activeTab,
    hasUnsavedChanges: hasUnsavedNewEmploymentChanges,
    isCloseDisabled: panelState.isSaving || actions.isDeletingWorker,
    isDiscardConfirmOpen: panelState.isDiscardConfirmOpen,
    isOpen: props.isOpen,
    handleCancelEdit: panelState.handleCancelEdit,
    handleTabChange: panelState.handleTabChange,
    onClose,
  });

  const selectedEmploymentActiveSalary =
    logic.currentEmployment?.salaries?.find((salary) => salary.isActive) ??
    null;

  const selectedEmploymentSalaryValue =
    selectedEmploymentActiveSalary?.value ?? logic.currentEmployment?.salary;

  const currentEmploymentForView: StaffProfileEmploymentCurrent | null =
    logic.currentEmployment == null
      ? null
      : {
          ...logic.currentEmployment,
          salary: selectedEmploymentSalaryValue ?? null,
        };

  const hasActiveEmployment =
    logic.profile?.employments.some((employment) => employment.isActive) ===
    true;

  const upcomingEmploymentStartDate =
    !hasActiveEmployment &&
    isFutureEmploymentStartDate(currentEmploymentForView?.startDate)
      ? (currentEmploymentForView?.startDate ?? null)
      : null;

  const showNoEmploymentBanner =
    !hasActiveEmployment && upcomingEmploymentStartDate == null;

  const endDateValue = currentEmploymentForView?.endDate;

  const canEndEmployment =
    hasActiveEmployment &&
    (endDateValue == null || endDateValue.startsWith("9999"));

  const handleTeamSelectionChange = useCallback(
    (teamIds: string[]): void => {
      updatePersonalField("teamIds", teamIds);
    },
    [updatePersonalField],
  );
  if (!props.isOpen) return null;

  const isProfileEditMode = panelState.editingTab === "profile";
  const isEmploymentEditMode = panelState.editingTab === "employmentSalary";
  const footerEditingTab =
    panelState.editingTab === panelState.activeTab
      ? panelState.editingTab
      : null;

  const isCreatingEmployment = panelState.employmentSaveMode === "create";
  const isEditLocked =
    logic.isSelfRegistered && panelState.activeTab === "profile";
  const hasEmploymentForView = currentEmploymentForView != null;
  const showEditButton =
    panelState.activeTab !== "agreements" &&
    (panelState.activeTab !== "employmentSalary" || hasEmploymentForView);

  const isEndEmploymentForCreateFlow =
    panelState.isEndEmploymentForCreateModalOpen;

  const endEmploymentModalFlowKey = isEndEmploymentForCreateFlow
    ? "create-flow"
    : "end-flow";

  const isEndEmploymentModalOpen =
    panelState.isEndEmploymentModalOpen ||
    panelState.isEndEmploymentForCreateModalOpen;

  const endEmploymentModalConfig = isEndEmploymentForCreateFlow
    ? {
        title: t("Staff.ProfilePanelCreateEmploymentModalTitle"),
        body: t("Staff.ProfilePanelCreateEmploymentModalBody"),
        onClose: panelState.handleCloseEndEmploymentForCreateModal,
        onConfirm: panelState.handleConfirmEndEmploymentForCreateModal,
      }
    : {
        title: t("Staff.ProfilePanelEndEmploymentModalTitle"),
        body: t("Staff.ProfilePanelEndEmploymentModalBody"),
        onClose: panelState.handleCloseEndEmploymentModal,
        onConfirm: panelState.handleConfirmEndEmploymentModal,
      };

  return (
    <>
      <Prompt
        when={hasUnsavedNewEmploymentChanges}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />
      <SidePanel
        isOpen={props.isOpen}
        onClose={unsavedEmploymentGuard.requestPanelClose}
        onBackdropClick={unsavedEmploymentGuard.requestPanelClose}
        header={
          <ProfilePanelHeader
            avatarUrl={avatarUrl}
            fullName={logic.fullName}
            primaryTeamLabel={logic.primaryTeamLabel}
            primaryRoleLabel={logic.primaryRoleLabel}
            additionalTagCount={logic.additionalTagCount}
            teamLabels={logic.teamLabels}
            onClose={unsavedEmploymentGuard.requestPanelClose}
          />
        }
        footer={
          <ProfilePanelFooter
            saveError={
              panelState.activeTab === "history" ||
              panelState.activeTab === "agreements" ||
              panelState.isSalaryModalOpen ||
              panelState.isTaxModalOpen ||
              panelState.isPaymentMethodModalOpen ||
              panelState.isChangeToPermanentModalOpen
                ? ""
                : panelState.saveError
            }
            isEditLocked={isEditLocked}
            canEdit={
              panelState.activeTab !== "history" &&
              panelState.activeTab !== "agreements"
            }
            showEditButton={showEditButton}
            canSaveProfileEdit={logic.profile?.isRegistered === false}
            canSavePersonalEdit={panelState.canSavePersonalEdit}
            canSaveEmploymentEdit={panelState.canSaveEmploymentEdit}
            showLockedEditHint={panelState.showLockedEditHint}
            isSaving={panelState.isSaving}
            isDeleting={actions.isDeletingWorker}
            editingTab={footerEditingTab}
            showAddEmploymentButton={
              panelState.activeTab === "employmentSalary" &&
              panelState.editingTab == null
            }
            showDeleteButton={
              panelState.activeTab === "profile" &&
              panelState.editingTab == null &&
              logic.profile?.isDeletable === true
            }
            onSetLockedEditHint={panelState.setShowLockedEditHint}
            onEditClick={panelState.handleEditClick}
            onAddEmploymentClick={panelState.handleAddEmploymentClick}
            showEndEmploymentButton={
              panelState.activeTab === "employmentSalary" &&
              panelState.editingTab == null &&
              canEndEmployment
            }
            onEndEmploymentClick={panelState.handleOpenEndEmploymentModal}
            onDeleteClick={actions.handleOpenDeleteModal}
            onCancelEdit={unsavedEmploymentGuard.requestCancelEdit}
            onOpenSaveModal={panelState.handleOpenSaveModal}
          />
        }
      >
        {logic.showMissingPersonalNumberWarning && (
          <WarningBanner>
            <WarningTitle>
              {t("Staff.ProfilePanelMissingPersonalNumberTitle")}
            </WarningTitle>
            <WarningBody>
              {t("Staff.ProfilePanelMissingPersonalNumberBody")}
            </WarningBody>
          </WarningBanner>
        )}

        <ProfilePanelTabs
          value={panelState.activeTab}
          onChange={unsavedEmploymentGuard.requestTabChange}
        />

        {logic.hasLoadError ? (
          <Message>{t("Staff.ProfilePanelLoadError")}</Message>
        ) : logic.isLoading || logic.profile == null ? (
          <Message>{t("Staff.ProfilePanelLoading")}</Message>
        ) : (
          <>
            {panelState.activeTab === "profile" && (
              <ProfilePanelProfileSection
                isEditMode={isProfileEditMode}
                editedPersonalDetails={panelState.editedPersonalDetails}
                profile={logic.profile}
                teamLabels={logic.teamLabels}
                hasStatistics={logic.hasStatistics}
                ratingLabel={logic.ratingLabel}
                ratingCountLabel={logic.ratingCountLabel}
                workedHoursLabel={logic.workedHoursLabel}
                teamOptions={panelState.teamOptions}
                onPersonalFieldChange={panelState.updatePersonalField}
                onOpenChangeTeamModal={actions.handleOpenChangeTeamModal}
              />
            )}

            {panelState.activeTab === "employmentSalary" && (
              <ProfilePanelEmploymentSection
                isEditMode={isEmploymentEditMode}
                isCreateMode={isCreatingEmployment}
                editedEmploymentDetails={panelState.editedEmploymentDetails}
                profile={logic.profile}
                currentEmployment={currentEmploymentForView}
                upcomingEmploymentStartDate={upcomingEmploymentStartDate}
                showNoEmploymentBanner={showNoEmploymentBanner}
                employmentRules={panelState.employmentRules}
                employmentOptions={panelState.employmentOptions}
                employmentTemplates={panelState.employmentTemplates}
                isLoadingEmploymentTemplates={
                  panelState.isLoadingEmploymentTemplates
                }
                isSaving={panelState.isSaving}
                selectedEmploymentTemplateId={
                  panelState.selectedEmploymentTemplateId
                }
                templateSalaries={panelState.templateSalaries}
                selectedTemplateSalaryId={panelState.selectedTemplateSalaryId}
                onEmploymentFieldChange={panelState.updateEmploymentField}
                isLoadingEmployeeNumber={panelState.isLoadingEmployeeNumber}
                onFetchEmployeeNumber={panelState.fetchEmployeeNumber}
                onSelectEmploymentTemplate={panelState.applyEmploymentTemplate}
                onSelectTemplateSalary={
                  panelState.applyEmploymentTemplateSalary
                }
                onChangeSalaryClick={panelState.handleOpenSalaryModal}
                onChangeTaxClick={panelState.handleOpenTaxModal}
                onChangePaymentMethodClick={
                  panelState.handleOpenPaymentMethodModal
                }
                onChangeToPermanentClick={
                  panelState.handleOpenChangeToPermanentModal
                }
              />
            )}

            {panelState.activeTab === "history" && (
              <ProfilePanelHistorySection
                employments={logic.profile.employments}
                isDeletingEmployment={actions.isDeletingFutureEmployment}
                onDeleteFutureEmployment={actions.handleDeleteFutureEmployment}
              />
            )}

            {panelState.activeTab === "agreements" && (
              <ProfilePanelAgreementsSection
                profile={logic.profile}
                companyId={props.companyId}
                workerId={props.workerId}
              />
            )}
          </>
        )}
      </SidePanel>

      <ProfilePanelSalaryModal
        isOpen={panelState.isSalaryModalOpen}
        isSaving={panelState.isSaving}
        saveError={panelState.saveError}
        editedEmploymentDetails={panelState.editedEmploymentDetails}
        effectiveFromDate={panelState.effectiveFromDate}
        earliestSelectableDate={panelState.salaryEarliestStartDate}
        salaryHistory={panelState.salaryDraftHistory}
        salaryType={logic.currentEmployment?.salaryType}
        onChangeFromDate={panelState.setEffectiveFromDate}
        onEmploymentFieldChange={panelState.updateEmploymentField}
        onClose={panelState.handleCloseSalaryModal}
        onConfirm={panelState.handleConfirmSalaryModal}
        onAddSalary={panelState.handleAddSalaryModal}
        onDeleteSalary={panelState.handleDeleteSalaryModal}
        onUpdateSalaryValue={panelState.handleUpdateSalaryDraftValue}
      />
      <ProfilePanelTaxModal
        isOpen={panelState.isTaxModalOpen}
        isSaving={panelState.isSaving}
        canFetchTaxTable={panelState.canFetchTaxTable}
        isFetchingTaxTable={panelState.isFetchingTaxTable}
        isTaxTableFetched={panelState.isTaxTableFetched}
        saveError={panelState.saveError}
        taxTable={panelState.editedEmploymentDetails.taxTable}
        effectiveFromDate={panelState.effectiveFromDate}
        earliestSelectableDate={panelState.salaryEarliestStartDate}
        employmentStartDate={logic.currentEmployment?.startDate ?? null}
        taxOptions={panelState.employmentOptions.taxTableOptions}
        taxHistory={panelState.taxDraftHistory}
        onChangeFromDate={panelState.setEffectiveFromDate}
        onTaxTableChange={(taxTable: string) =>
          panelState.updateEmploymentField("taxTable", taxTable)
        }
        onClose={panelState.handleCloseTaxModal}
        onConfirm={panelState.handleConfirmTaxModal}
        onAddTax={panelState.handleAddTaxModal}
        onDeleteTax={panelState.handleDeleteTaxModal}
        onFetchTaxTable={panelState.handleFetchTaxTable}
        onUpdateTaxValue={panelState.handleUpdateTaxDraftValue}
      />
      <ProfilePanelPaymentMethodModal
        isOpen={panelState.isPaymentMethodModalOpen}
        isSaving={panelState.isSaving}
        canSavePaymentMethod={panelState.canSavePaymentMethod}
        saveError={panelState.saveError}
        paymentMethodError={panelState.paymentMethodError}
        paymentMethodDetails={panelState.editedPaymentMethodDetails}
        paymentMethodOptions={panelState.paymentMethodOptions}
        hasPaymentMethod={(logic.profile?.paymentMethods.length ?? 0) > 0}
        onClose={panelState.handleClosePaymentMethodModal}
        onConfirm={panelState.handleConfirmPaymentMethodModal}
        onDelete={panelState.handleDeletePaymentMethodModal}
        onPaymentMethodFieldChange={panelState.updatePaymentMethodField}
      />
      <ProfilePanelTeamModal
        isOpen={actions.isTeamModalOpen}
        isSaving={actions.isUpdatingTeams}
        errorMessage={actions.teamUpdateError}
        selectedTeamIds={panelState.editedPersonalDetails.teamIds}
        teamOptions={panelState.teamOptions}
        onSelectedValuesChange={handleTeamSelectionChange}
        onClose={actions.handleCloseChangeTeamModal}
        onConfirm={actions.handleConfirmChangeTeamModal}
      />
      <ProfilePanelDeleteModal
        isOpen={actions.isDeleteModalOpen}
        isDeleting={actions.isDeletingWorker}
        onClose={actions.handleCloseDeleteModal}
        onConfirm={actions.handleConfirmDeleteWorker}
      />
      <ProfilePanelEndEmploymentModal
        key={endEmploymentModalFlowKey}
        isOpen={isEndEmploymentModalOpen}
        isSaving={panelState.isSaving}
        endEmploymentDate={panelState.endEmploymentDate}
        employmentStartDate={panelState.salaryEarliestStartDate}
        title={endEmploymentModalConfig.title}
        body={endEmploymentModalConfig.body}
        onClose={endEmploymentModalConfig.onClose}
        onConfirm={endEmploymentModalConfig.onConfirm}
        onEndEmploymentDateChange={panelState.setEndEmploymentDate}
      />
      <ProfilePanelChangeToPermanentModal
        isOpen={panelState.isChangeToPermanentModalOpen}
        isSaving={panelState.isSaving}
        saveError={panelState.saveError}
        onClose={panelState.handleCloseChangeToPermanentModal}
        onConfirm={panelState.handleConfirmChangeToPermanent}
      />
      <Modal
        isOpen={panelState.isDiscardConfirmOpen}
        onClose={panelState.handleCancelDiscardClose}
        size="compact"
        variant="confirm"
        label={t("DesignComponents.ConfirmCloseTitle")}
        footer={
          <>
            <SecondaryButton
              label={t("DesignComponents.Cancel")}
              onClick={panelState.handleCancelDiscardClose}
            />
            <PrimaryButton
              label={t("DesignComponents.Close")}
              onClick={panelState.handleConfirmDiscardClose}
            />
          </>
        }
      >
        {t("DesignComponents.ConfirmCloseBody")}
      </Modal>
      <Modal
        isOpen={unsavedEmploymentGuard.isConfirmOpen}
        onClose={unsavedEmploymentGuard.cancelDiscard}
        size="compact"
        variant="confirm"
        showCloseButton={false}
        label={t("Staff.ProfilePanelDiscardEmploymentTitle")}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={unsavedEmploymentGuard.cancelDiscard}
            />
            <PrimaryButton
              label={t("Staff.ProfilePanelDiscardEmploymentAction")}
              onClick={unsavedEmploymentGuard.confirmDiscard}
            />
          </>
        }
      >
        {t("Staff.ProfilePanelDiscardEmploymentBody")}
      </Modal>
    </>
  );
};
